// Barra de navegación principal de SubiGol
import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu, X, Sun, Moon, LogOut, User,
  Trophy, BarChart3, Users, Zap, Shield,
  CalendarDays, Layers, Settings, LayoutDashboard,
  Coins, Landmark, History, ArrowUpCircle, Download,
  Tv, Receipt, Megaphone, KeyRound, Bell
} from 'lucide-react'

import useAuthStore from '../../store/useAuthStore'
import useThemeStore from '../../store/useThemeStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from '../../api/client'
import useBetStore from '../../store/useBetStore'
import Button from '../ui/Button'
import RechargeModal from './RechargeModal'

// Enlaces de navegación principal (excluyendo los que ya están en la barra inferior móvil)
const navLinks = [
  { path: '/movimientos', label: 'Movimientos', icon: History },
  { path: '/historial-billetera', label: 'Depósitos y Retiros', icon: Landmark },
  { path: '/ranking', label: 'Ranking', icon: BarChart3 },
  { path: '/comunidades', label: 'Comunidades', icon: Users },
  { path: '/estadisticas', label: 'Estadísticas', icon: BarChart3 },
]

export default function Navbar() {
  const queryClient = useQueryClient()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isAuthenticated, user, logout } = useAuthStore()
  const { theme, toggleTheme } = useThemeStore()
  const { credits, fetchBalance, rechargeModalOpen, setRechargeModalOpen } = useBetStore()
  const location = useLocation()
  const navigate = useNavigate()

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })


  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const notificationsRef = useRef(null)

  const [userNotificationsOpen, setUserNotificationsOpen] = useState(false)
  const userNotificationsRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false)
      }
      if (userNotificationsRef.current && !userNotificationsRef.current.contains(event.target)) {
        setUserNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const hasAdminNotificationsAccess = useMemo(() => {
    if (!isAuthenticated || !user) return false
    const rol = user.rol
    if (rol === 'admin' || rol === 'superadmin') return true
    
    const permisos = user.permisos || {}
    return permisos.aprobar_recargas === 1 || permisos.aprobar_retiros === 1
  }, [isAuthenticated, user])

  const { data: notificationsData } = useQuery({
    queryKey: ['adminNotifications'],
    queryFn: async () => {
      const response = await client.get('/admin/notifications/pending')
      return response.data?.datos || { unread_count: 0, recharges_count: 0, withdrawals_count: 0, items: [] }
    },
    enabled: !!hasAdminNotificationsAccess,
    refetchInterval: 30000,
  })

  // Notificaciones de Usuario
  const { data: userNotificationsData } = useQuery({
    queryKey: ['userNotifications'],
    queryFn: async () => {
      const response = await client.get('/notifications/my')
      return response.data?.datos || []
    },
    enabled: isAuthenticated,
    refetchInterval: 30000,
    staleTime: 10000,
  })

  const userNotifications = userNotificationsData || []
  const userUnreadCount = userNotifications.length

  const readAllMutation = useMutation({
    mutationFn: async () => {
      return (await client.post('/notifications/read-all')).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotifications'] })
    }
  })

  const prevUserUnreadCountRef = useRef(0)

  // Sonido de alerta sutil sintetizado usando Web Audio API
  const playNotificationSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return
      const ctx = new AudioContext()
      
      // Nota 1 (Chime sutil D5)
      const osc1 = ctx.createOscillator()
      const gain1 = ctx.createGain()
      osc1.type = 'sine'
      osc1.frequency.setValueAtTime(587.33, ctx.currentTime)
      gain1.gain.setValueAtTime(0.08, ctx.currentTime)
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35)
      osc1.connect(gain1)
      gain1.connect(ctx.destination)
      osc1.start()
      osc1.stop(ctx.currentTime + 0.35)

      // Nota 2 (Chime sutil A5 con retardo)
      const osc2 = ctx.createOscillator()
      const gain2 = ctx.createGain()
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.08)
      gain2.gain.setValueAtTime(0, ctx.currentTime)
      gain2.gain.setValueAtTime(0.08, ctx.currentTime + 0.08)
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45)
      osc2.connect(gain2)
      gain2.connect(ctx.destination)
      osc2.start(ctx.currentTime + 0.08)
      osc2.stop(ctx.currentTime + 0.45)
    } catch (e) {
      console.warn('AudioContext not allowed or not supported', e)
    }
  }

  const prevUnreadCountRef = useRef(0)

  // Solicitar permiso de notificaciones nativas de escritorio si tiene acceso
  useEffect(() => {
    if (hasAdminNotificationsAccess && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission()
      }
    }
  }, [hasAdminNotificationsAccess])

  useEffect(() => {
    if (notificationsData) {
      const currentCount = notificationsData.unread_count
      const prevCount = prevUnreadCountRef.current
      
      // Solo reproducir sonido y notificar si la cantidad de notificaciones aumentó
      if (currentCount > prevCount) {
        playNotificationSound()

        // Enviar notificación de escritorio nativa si hay permiso
        if ('Notification' in window && Notification.permission === 'granted') {
          const totalNew = currentCount - prevCount
          const logoPath = settings?.logo_sistema || '/favicon.svg'
          const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
            ? import.meta.env.VITE_API_URL.replace('/api', '')
            : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
          const fullLogoUrl = logoPath.startsWith('/uploads') 
            ? `${baseImgUrl}${logoPath}` 
            : (logoPath.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoPath.substring(1)}` : logoPath)

          try {
            const notification = new Notification('Solicitudes Pendientes - SubiGol 🏆', {
              body: `Tienes ${totalNew} nueva(s) solicitud(es) de recarga o retiro por aprobar.`,
              icon: fullLogoUrl,
              silent: true // Ya estamos reproduciendo el sonido sintetizado personalizado
            })

            notification.onclick = () => {
              window.focus()
              navigate('/admin')
            }
          } catch (e) {
            console.warn('Error al disparar notificación nativa:', e)
          }
        }
      }
      
      prevUnreadCountRef.current = currentCount
    }
  }, [notificationsData, settings, navigate])

  useEffect(() => {
    if (userNotifications) {
      const currentCount = userUnreadCount
      const prevCount = prevUserUnreadCountRef.current
      
      if (currentCount > prevCount) {
        playNotificationSound()
      }
      
      prevUserUnreadCountRef.current = currentCount
    }
  }, [userNotifications, userUnreadCount])

  useEffect(() => {
    if (settings) {
      const nombre = settings.nombre_sistema || 'SubiGol'
      const suffix = ' — Predicciones Mundial 2026'
      if (!document.title.includes(nombre)) {
        document.title = `${nombre}${suffix}`
      }

      const logoPath = settings.logo_sistema || '/favicon.svg'
      const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
        ? import.meta.env.VITE_API_URL.replace('/api', '')
        : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
      const fullLogoUrl = logoPath.startsWith('/uploads') ? `${baseImgUrl}${logoPath}` : (logoPath.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoPath.substring(1)}` : logoPath)

      let linkElement = document.querySelector("link[rel~='icon']")
      if (!linkElement) {
        linkElement = document.createElement('link')
        linkElement.rel = 'icon'
        document.getElementsByTagName('head')[0].appendChild(linkElement)
      }
      
      linkElement.href = fullLogoUrl
      
      if (logoPath.endsWith('.svg')) {
        linkElement.type = 'image/svg+xml'
      } else if (logoPath.endsWith('.png')) {
        linkElement.type = 'image/png'
      } else {
        linkElement.type = 'image/x-icon'
      }
    }
  }, [settings])

  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showiOSModal, setShowiOSModal] = useState(false)
  const [showAndroidModal, setShowAndroidModal] = useState(false)

  const isStandaloneGlobal = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');

  useEffect(() => {
    if (isAuthenticated) {
      fetchBalance().catch(() => {})
    }
  }, [isAuthenticated])

  useEffect(() => {
    // 1. Detectar si ya está instalada o se ejecuta en standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    if (isStandalone) {
      setIsInstalled(true);
    }

    // 2. Comprobar si el evento ya fue capturado globalmente
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setIsInstallable(true);
    }

    // 3. Escuchar el evento custom y el evento nativo
    const handlePromptAvailable = () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
        setIsInstallable(true);
      }
    };

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    };

    window.addEventListener('pwa-prompt-available', handlePromptAvailable);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt || window.deferredPrompt;
    if (!promptEvent) {
      // Si no hay prompt nativo, mostrar instrucciones según el sistema operativo
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isiOS) {
        setShowiOSModal(true);
      } else {
        setShowAndroidModal(true);
      }
      return;
    }
    
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
      window.deferredPrompt = null;
      setDeferredPrompt(null);
    }
  };

  const showInstallButton = !isInstalled && !isStandaloneGlobal;

  const hasAdminAccess = user?.rol === 'admin' || user?.rol === 'superadmin' || (user?.permisos && Object.values(user.permisos).some(val => val === 1))
  const isAdminPath = location.pathname.startsWith('/admin')

  // Enlaces para la barra de navegación de escritorio (vacío en favor del sidebar para evitar duplicados)
  const desktopLinks = []

  // Enlaces para el menú móvil
  const mobileLinks = useMemo(() => {
    if (isAdminPath) {
      const permissionMap = {
        'resultados': 'gestionar_resultados',
        'partidos': 'gestionar_partidos',
        'equipos': 'gestionar_equipos',
        'jugadores': 'gestionar_jugadores',
        'usuarios': 'gestionar_usuarios',
        'recargas': 'aprobar_recargas',
        'retiros': 'aprobar_retiros',
        'finanzas': 'gestionar_finanzas',
        'apuestas': 'gestionar_apuestas',
        'transmisiones': 'gestionar_canales',
        'publicidad': 'gestionar_publicidad',
        'configuracion': 'gestionar_configuracion',
      }

      const items = [
        { path: '/admin?tab=dashboard', label: 'Dashboard', icon: BarChart3 },
        { path: '/admin?tab=resultados', label: 'Marcadores', icon: Trophy },
        { path: '/admin?tab=partidos', label: 'Partidos', icon: CalendarDays },
        { path: '/admin?tab=equipos', label: 'Equipos', icon: Layers },
        { path: '/admin?tab=jugadores', label: 'Jugadores', icon: Users },
        { path: '/admin?tab=usuarios', label: 'Usuarios', icon: Shield },
        { path: '/admin?tab=permisos', label: 'Permisos', icon: KeyRound },
        { path: '/admin?tab=recargas', label: 'Recargas', icon: Landmark },
        { path: '/admin?tab=retiros', label: 'Retiros', icon: ArrowUpCircle },
        { path: '/admin?tab=finanzas', label: 'Finanzas', icon: Receipt },
        { path: '/admin?tab=apuestas', label: 'Apuestas', icon: Coins },
        { path: '/admin?tab=transmisiones', label: 'Transmisiones', icon: Tv },
        { path: '/admin?tab=publicidad', label: 'Publicidad', icon: Megaphone },
        { path: '/admin?tab=configuracion', label: 'Configuración', icon: Settings },
      ]

      if (user?.rol === 'superadmin' || user?.rol === 'admin') {
        items.push({ path: '/admin?tab=historial-apuestas', label: 'Historial Apuestas', icon: History })
      }

      let filteredItems = [...items]
      if (user?.rol !== 'admin' && user?.rol !== 'superadmin') {
        filteredItems = items.filter(item => {
          if (item.path.includes('tab=dashboard')) return true
          
          const match = item.path.match(/tab=([^&]+)/)
          const tab = match ? match[1] : null
          
          if (tab === 'permisos') return false
          
          const requiredPermission = permissionMap[tab]
          return requiredPermission && user?.permisos && user.permisos[requiredPermission] === 1
        })

        // Agregar "Gastos y Caja" para el rol empleado
        if (user?.rol === 'empleado') {
          filteredItems.push({ path: '/finanzas-personal', label: 'Gastos y Caja', icon: Receipt })
        }
      }

      filteredItems.push({ path: '/dashboard', label: 'Volver a la App', icon: LayoutDashboard })
      return filteredItems
    }

    const baseLinks = [...navLinks]

    return [
      ...baseLinks,
      { path: '/perfil', label: 'Mi Perfil', icon: User },
      ...(hasAdminAccess ? [{ path: '/admin', label: 'Administración', icon: Shield }] : [])
    ];
  }, [isAdminPath, user, hasAdminAccess])

  const handleLogout = () => {
    logout()
    navigate('/login')
    setMobileMenuOpen(false)
  }

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const logoSistema = settings?.logo_sistema || '/favicon.svg'
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const logoUrl = logoSistema.startsWith('/uploads') ? `${baseImgUrl}${logoSistema}` : (logoSistema.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoSistema.substring(1)}` : logoSistema)

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-dark border-b border-border-subtle shadow-md">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2.5 group">
            <img
              src={logoUrl}
              alt="Logo"
              className="w-9 h-9 object-contain transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
            />
            <span className="font-display font-bold text-lg text-text-primary hidden sm:block">
              {nombreSistema}
            </span>
          </Link>

          {/* Navegación escritorio */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-1 ml-4 md:ml-8 lg:ml-12">
              {desktopLinks.map(link => {
                const currentTab = new URLSearchParams(location.search).get('tab') || 'dashboard'
                const isActive = link.path.includes('tab=')
                  ? location.pathname.startsWith('/admin') && link.path.includes(`tab=${currentTab}`)
                  : location.pathname === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                      relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium font-body
                      transition-colors duration-200
                      ${isActive
                        ? 'text-cyan'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                      }
                    `}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="nav-indicator"
                        className="absolute -bottom-[1px] left-3 right-3 h-0.5 bg-cyan rounded-full"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                )
              })}
            </div>
          )}

          {!isAuthenticated && (
            <div className="hidden md:flex items-center gap-2 ml-4 md:ml-8 lg:ml-12">
              <Link
                to="/"
                className={`px-3 py-2 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                  location.pathname === '/' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                }`}
              >
                Inicio
              </Link>
              <Link
                to="/partidos"
                className={`px-3 py-2 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                  location.pathname === '/partidos' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                }`}
              >
                Partidos
              </Link>
              <Link
                to="/clasificacion"
                className={`px-3 py-2 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                  location.pathname === '/clasificacion' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                }`}
              >
                Clasificación
              </Link>
            </div>
          )}

          {/* Acciones de la derecha */}
          <div className="flex items-center gap-2">
            {/* Descargar aplicación */}
            {showInstallButton && (
              <button
                onClick={handleInstallClick}
                className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors"
                title="Descargar aplicación"
                aria-label="Descargar aplicación"
              >
                <Download className="w-5 h-5 text-cyan animate-pulse" />
              </button>
            )}

            {/* Campana de Notificaciones de Usuario */}
            {isAuthenticated && (
              <div className="relative" ref={userNotificationsRef}>
                <button
                  onClick={() => setUserNotificationsOpen(!userNotificationsOpen)}
                  className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors relative"
                  aria-label="Notificaciones de usuario"
                >
                  <Bell className="w-5 h-5 text-text-muted hover:text-text-primary" />
                  {userUnreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4.5 h-4.5 bg-cyan text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-surface-card animate-pulse px-1">
                      {userUnreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {userNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute -left-16 md:left-auto md:right-0 mt-2 w-72 md:w-80 rounded-xl bg-white dark:bg-navy-dark border border-border-subtle shadow-2xl py-3 px-4 z-50 space-y-3 font-body text-xs"
                    >
                      <div className="flex justify-between items-center border-b border-border-subtle pb-2">
                        <h4 className="font-display font-bold text-sm text-text-primary">Mis Notificaciones</h4>
                        {userUnreadCount > 0 && (
                          <button
                            onClick={() => readAllMutation.mutate()}
                            className="text-[10px] text-cyan hover:underline font-bold transition-all font-display"
                            disabled={readAllMutation.isPending}
                          >
                            Marcar todo como leído
                          </button>
                        )}
                      </div>

                      <div className="space-y-2.5 max-h-[250px] overflow-y-auto pr-1">
                        {userNotifications.length > 0 ? (
                          userNotifications.map((notif) => (
                            <div
                              key={notif.id}
                              onClick={async () => {
                                try {
                                  await client.post(`/notifications/${notif.id}/read`)
                                  queryClient.invalidateQueries({ queryKey: ['userNotifications'] })
                                } catch (e) {
                                  console.error(e)
                                }
                              }}
                              className="flex flex-col gap-1 p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-navy-light/40 border border-transparent hover:border-border-subtle/50 transition-all cursor-pointer bg-slate-50 dark:bg-navy/40"
                            >
                              <div className="flex justify-between items-start gap-2">
                                <span className="text-[10px] text-text-primary font-medium flex-1">
                                  {notif.mensaje}
                                </span>
                                <span className="text-[9px] text-text-muted whitespace-nowrap shrink-0 mt-0.5">
                                  {new Date(notif.creado_en.replace(' ', 'T')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-text-muted">
                            No tienes notificaciones nuevas 🎉
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Alternar tema */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors"
              aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Campana de Notificaciones Administrativas */}
            {hasAdminNotificationsAccess && notificationsData && (
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors relative"
                  aria-label="Notificaciones de administración"
                >
                  <Bell className="w-5 h-5" />
                  {notificationsData.unread_count > 0 && (
                    <span className="absolute top-1 right-1 w-4.5 h-4.5 bg-error text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-surface-card animate-pulse px-1">
                      {notificationsData.unread_count}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {notificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute -left-16 md:left-auto md:right-0 mt-2 w-72 md:w-80 rounded-xl bg-white dark:bg-navy-dark border border-border-subtle shadow-2xl py-3 px-4 z-50 space-y-3 font-body text-xs"
                    >
                      <div className="flex justify-between items-center border-b border-border-subtle pb-2">
                        <h4 className="font-display font-bold text-sm text-text-primary">Notificaciones Admin</h4>
                        <span className="text-[10px] bg-cyan/10 text-cyan px-2 py-0.5 rounded-full font-bold">
                          {notificationsData.unread_count} pendientes
                        </span>
                      </div>

                      <div className="space-y-2.5 max-h-[250px] overflow-y-auto pr-1">
                        {notificationsData.items && notificationsData.items.length > 0 ? (
                          notificationsData.items.map((item) => (
                            <div
                              key={`${item.tipo}-${item.id}`}
                              onClick={() => {
                                setNotificationsOpen(false)
                                navigate(item.tipo === 'recarga' ? '/admin?tab=recargas' : '/admin?tab=retiros')
                              }}
                              className="flex flex-col gap-1 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-navy-light/40 border border-transparent hover:border-border-subtle/50 transition-all cursor-pointer bg-slate-50 dark:bg-navy/40"
                            >
                              <div className="flex justify-between items-start">
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${
                                  item.tipo === 'recarga' ? 'bg-cyan/10 text-cyan' : 'bg-warning/10 text-warning'
                                }`}>
                                  {item.tipo === 'recarga' ? 'Depósito' : 'Retiro'}
                                </span>
                                <span className="text-[9px] text-text-muted">
                                  {new Date(item.creado_en.replace(' ', 'T')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <p className="text-[11px] text-text-primary font-medium">
                                {item.usuario_nombre} {item.usuario_apellido}
                              </p>
                              <p className="text-[10px] text-text-secondary">
                                Monto: <strong className="font-semibold text-text-primary">{Number(item.monto).toFixed(2)} Bs</strong>
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-6 text-text-muted">
                            No hay solicitudes pendientes 🎉
                          </div>
                        )}
                      </div>

                      <div className="border-t border-border-subtle pt-2.5 flex flex-col gap-1.5">
                        {notificationsData.recharges_count > 0 && (
                          <Link
                            to="/admin?tab=recargas"
                            onClick={() => setNotificationsOpen(false)}
                            className="flex justify-between items-center px-2 py-1.5 rounded bg-cyan/5 hover:bg-cyan/10 text-cyan transition-all"
                          >
                            <span>Recargas pendientes</span>
                            <span className="font-bold font-mono">{notificationsData.recharges_count}</span>
                          </Link>
                        )}
                        {notificationsData.withdrawals_count > 0 && (
                          <Link
                            to="/admin?tab=retiros"
                            onClick={() => setNotificationsOpen(false)}
                            className="flex justify-between items-center px-2 py-1.5 rounded bg-warning/5 hover:bg-warning/10 text-warning transition-all"
                          >
                            <span>Retiros pendientes</span>
                            <span className="font-bold font-mono">{notificationsData.withdrawals_count}</span>
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}


            {isAuthenticated ? (
              <>
                {/* Menú de usuario en escritorio */}
                <div className="hidden md:flex items-center gap-3">
                  <button
                    onClick={() => setRechargeModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-card border border-border-subtle hover:border-cyan/50 hover:bg-cyan/5 transition-all text-cyan font-display font-semibold text-sm"
                  >
                    <Coins className="w-4 h-4 text-cyan" />
                    <span>{credits.toFixed(2)} Bs</span>
                  </button>

                  <Link
                    to="/perfil"
                    className="flex items-center gap-2 px-2.5 py-1 rounded-lg hover:bg-surface-card transition-all border border-transparent hover:border-cyan/15"
                  >
                    <div className="w-7 h-7 rounded-full bg-cyan/10 border border-cyan/20 overflow-hidden flex items-center justify-center shrink-0">
                      {user?.avatar ? (
                        <img
                          src={user.avatar.startsWith('http') ? user.avatar : `${baseImgUrl}${user.avatar}`}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-display font-semibold text-[10px] text-cyan uppercase">
                          {((user?.nombre?.[0] || '') + (user?.apellido?.[0] || '')).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-medium text-text-primary max-w-[120px] truncate">
                      {user?.nombre || 'Usuario'}
                    </span>
                  </Link>
                   <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-secondary hover:text-error hover:bg-error/10 border border-transparent hover:border-error/25 transition-all text-xs font-semibold"
                    aria-label="Cerrar sesión"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Salir</span>
                  </button>
                </div>
                {/* Saldo y Depositar en móvil */}
                <div className="flex md:hidden items-center gap-1.5 mr-1 animate-fade-in">
                  <button
                    onClick={() => setRechargeModalOpen(true)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-surface-card/85 border border-border-subtle hover:border-cyan/50 text-cyan font-display font-semibold text-xs"
                  >
                    <Coins className="w-3.5 h-3.5 text-cyan shrink-0" />
                    <span>{credits.toFixed(2)} Bs</span>
                  </button>
                  <button
                    onClick={() => setRechargeModalOpen(true)}
                    className="px-2.5 py-1.5 rounded-lg bg-cyan text-navy-dark font-display font-bold text-[11px] shadow-md shadow-cyan/10 hover:bg-cyan-light transition-all active:scale-95 whitespace-nowrap"
                  >
                    Depositar
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">Iniciar sesión</Button>
                </Link>
                <Link to="/registro" className="hidden sm:block">
                  <Button size="sm">Registrarse</Button>
                </Link>
              </div>
            )}

            {/* Botón menú móvil (visible para todos en móvil) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors ml-1"
              aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Menú móvil desplegable */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border-subtle overflow-hidden"
          >
            <div className="px-4 py-3 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center justify-between px-3 py-2 bg-surface-card rounded-lg mb-2 border border-border-subtle">
                    <div className="flex items-center gap-2 text-cyan">
                      <Coins className="w-4 h-4" />
                      <span className="text-sm font-display font-medium text-text-primary">Mi Saldo:</span>
                    </div>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        setRechargeModalOpen(true)
                      }}
                      className="text-sm font-semibold text-cyan bg-cyan/15 px-2.5 py-1 rounded-md border border-cyan/20 hover:bg-cyan/25 transition-colors"
                    >
                      {credits.toFixed(2)} Bs
                    </button>
                  </div>

                  {mobileLinks.map(link => {
                    const currentTab = new URLSearchParams(location.search).get('tab') || 'dashboard'
                    const isActive = link.path.includes('tab=')
                      ? location.pathname.startsWith('/admin') && link.path.includes(`tab=${currentTab}`)
                      : location.pathname === link.path
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-body
                          transition-colors duration-200
                          ${isActive
                            ? 'text-cyan bg-cyan/10'
                            : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                          }
                        `}
                      >
                        <link.icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    )
                  })}
                  <hr className="border-border-subtle my-2" />
                  {showInstallButton && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        handleInstallClick()
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-cyan hover:bg-cyan/10 transition-colors w-full"
                    >
                      <Download className="w-4 h-4 text-cyan" />
                      Instalar Aplicación
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-error hover:bg-error/10 transition-colors w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                      location.pathname === '/' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                    }`}
                  >
                    Inicio
                  </Link>
                  <Link
                    to="/partidos"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                      location.pathname === '/partidos' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                    }`}
                  >
                    Partidos
                  </Link>
                  <Link
                    to="/clasificacion"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-body transition-colors duration-200 ${
                      location.pathname === '/clasificacion' ? 'text-cyan bg-cyan/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                    }`}
                  >
                    Clasificación
                  </Link>
                  <div className="border-t border-border-subtle my-2 pt-2 flex flex-col gap-2">
                    {showInstallButton && (
                      <Button 
                        onClick={() => { 
                          setMobileMenuOpen(false); 
                          handleInstallClick(); 
                        }} 
                        size="sm" 
                        variant="outline" 
                        className="w-full text-center flex items-center justify-center gap-2 border-cyan/30 text-cyan hover:bg-cyan/10"
                      >
                        <Download className="w-4 h-4" /> Instalar Aplicación
                      </Button>
                    )}
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full text-center">Iniciar sesión</Button>
                    </Link>
                    <Link to="/registro" onClick={() => setMobileMenuOpen(false)}>
                      <Button size="sm" className="w-full text-center">Registrarse</Button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      </nav>
      <RechargeModal isOpen={rechargeModalOpen} onClose={() => setRechargeModalOpen(false)} />

      {/* Modales de instrucciones de instalación */}
      {showiOSModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left">Instalar Aplicación en iOS</h3>
              <button onClick={() => setShowiOSModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary">
              <p>Sigue estos sencillos pasos para instalar <strong className="text-text-primary font-semibold">{nombreSistema}</strong> en tu iPhone o iPad:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs">
                <li>Presiona el botón de <strong>Compartir</strong> en la barra inferior de Safari (el icono de un cuadro con una flecha apuntando hacia arriba <span className="inline-block p-1 bg-white/10 rounded">📤</span>).</li>
                <li>Desplázate hacia abajo en el menú y selecciona la opción <strong>"Agregar a inicio"</strong> o <strong>"A la pantalla de inicio"</strong>.</li>
                <li>Presiona <strong>"Agregar"</strong> en la esquina superior derecha para confirmar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowiOSModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {showAndroidModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left">Instalar Aplicación</h3>
              <button onClick={() => setShowAndroidModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary">
              <p>Sigue estos pasos para instalar <strong className="text-text-primary font-semibold">{nombreSistema}</strong>:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs">
                <li>Presiona el botón de menú de tu navegador (los tres puntos en la esquina superior derecha).</li>
                <li>Selecciona la opción <strong>"Instalar aplicación"</strong> o <strong>"Agregar a la pantalla principal"</strong>.</li>
                <li>Confirma la acción para completar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowAndroidModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  )
}
